import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * A custom element that manages the "Complétez votre intérieur" cross-sell bundle.
 *
 * Lets shoppers toggle a curated set of complementary products (checked by
 * default), keeps a running total price of the checked items, and adds every
 * checked product's first available variant to the cart in a single Ajax Cart
 * API request (POST /cart/add.js). Following the same convention used by
 * `product-form.js`'s batch add-to-cart, it dispatches the theme's standard
 * `CartLinesUpdateEvent` so the existing cart-drawer (if present on the page)
 * auto-opens and refreshes itself - falling back to a redirect to /cart when
 * there is no drawer to open.
 *
 * @typedef {object} ProductBundleRefs
 * @property {HTMLElement} totalPrice - Element displaying the running total.
 * @property {HTMLButtonElement} addAllButton - The "Ajouter tout au panier" button.
 * @extends {Component<ProductBundleRefs>}
 */
class ProductBundleComponent extends Component {
  requiredRefs = ['totalPrice', 'addAllButton'];

  connectedCallback() {
    super.connectedCallback();
    this.#updateTotal();
  }

  /**
   * Handles a bundle item checkbox being toggled - recalculates the running total.
   */
  handleItemToggle() {
    this.#updateTotal();
  }

  /**
   * Handles the "Ajouter tout au panier" button click: adds every checked
   * product's variant to the cart in one request.
   */
  handleAddAll() {
    const checked = this.#getCheckedVariantIds();
    if (checked.length === 0) return;

    const { addAllButton } = this.refs;
    addAllButton.disabled = true;
    addAllButton.classList.add('product-bundle__button--loading');

    // Mirrors product-form.js: include every cart-items-component's section id
    // so the Ajax response carries fresh rendered HTML for the cart drawer/page.
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : null))
      .filter(Boolean);

    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: checked.map((variantId) => ({ merchandiseId: variantId, quantity: 1 })),
        promise: deferredEventPromise.promise,
      })
    );

    const payload = {
      items: checked.map((variantId) => ({ id: Number(variantId), quantity: 1 })),
      sections: sectionIds.join(','),
    };

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          const message = response.message || 'Add to cart failed';
          deferredEventPromise.reject(new Error(message));
          this.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
          return;
        }

        return this.#refreshCart().then((ajaxCart) => {
          deferredEventPromise.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
            detail: {
              items: ajaxCart.items,
              source: 'product-bundle-component',
              sections: response.sections,
              didError: false,
            },
          });

          // No cart drawer on this page to auto-open - send the shopper to /cart instead.
          if (!document.querySelector('cart-drawer-component')) {
            window.location.href = Theme.routes.cart_url;
          }
        });
      })
      .catch((error) => {
        console.error('[product-bundle] Add all to cart failed:', error);
        deferredEventPromise.reject(error);
        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Network error during add to cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
        // Still get the shopper to their cart even if the drawer refresh failed.
        window.location.href = Theme.routes.cart_url;
      })
      .finally(() => {
        addAllButton.disabled = false;
        addAllButton.classList.remove('product-bundle__button--loading');
      });
  }

  /**
   * Refreshes cart data, preferring the existing cart-items-component when present
   * (same pattern as product-form.js's #refreshCart).
   * @returns {Promise<any>}
   */
  #refreshCart() {
    const cartItemsComponent = document.querySelector('cart-items-component');
    if (cartItemsComponent && typeof cartItemsComponent.fetchCartData === 'function') {
      return cartItemsComponent.fetchCartData();
    }

    return fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((response) => {
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
      return response.json();
    });
  }

  /**
   * @returns {string[]} The variant ids of currently checked, available bundle items.
   */
  #getCheckedVariantIds() {
    return Array.from(this.querySelectorAll('.product-bundle__checkbox'))
      .filter((checkbox) => checkbox instanceof HTMLInputElement && checkbox.checked && checkbox.dataset.variantId)
      .map((checkbox) => /** @type {HTMLInputElement} */ (checkbox).dataset.variantId ?? '');
  }

  /**
   * Recalculates and displays the running total price of checked items.
   */
  #updateTotal() {
    const checkboxes = /** @type {HTMLInputElement[]} */ (
      Array.from(this.querySelectorAll('.product-bundle__checkbox'))
    );

    const totalCents = checkboxes.reduce((sum, checkbox) => {
      if (!checkbox.checked) return sum;
      return sum + (Number.parseInt(checkbox.dataset.price || '0', 10) || 0);
    }, 0);

    const format = this.dataset.moneyFormat || '{{ amount }}';
    const currency = this.dataset.currency || 'EUR';

    this.refs.totalPrice.textContent = formatMoney(totalCents, format, currency);

    const hasCheckedItems = checkboxes.some((checkbox) => checkbox.checked);
    this.refs.addAllButton.disabled = !hasCheckedItems;
  }
}

if (!customElements.get('product-bundle-component')) {
  customElements.define('product-bundle-component', ProductBundleComponent);
}
